import axios from "axios";
import { recordApi } from "./rum";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        "Content-Type": "application/json",
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    config.rumStart = performance.now();     // real-user timing (lib/rum.js)
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => {
        if (response.config?.rumStart != null) recordApi(response.config.url, performance.now() - response.config.rumStart);
        return response;
    },
    (error) => {
        // A wrong password or code on the sign-in page is just an error message.
        const signingIn = /\/auth\/(login|2fa\/verify)$/.test(error.config?.url || "");
        if (error.response?.status === 401 && !signingIn) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            window.location.href = "/";
        }
        return Promise.reject(error);
    }
);

// Auth
// "Remember this device for 30 days": one token per email, kept in this browser.
const DEVICE_KEY = "mfaDevices";
const readDevices = () => {
    try {
        return JSON.parse(localStorage.getItem(DEVICE_KEY) || "{}");
    } catch (_) {
        return {};
    }
};
export const rememberDevice = (email, token) => {
    try {
        localStorage.setItem(DEVICE_KEY, JSON.stringify({ ...readDevices(), [String(email).toLowerCase()]: token }));
    } catch (_) {
        /* private window: it just asks for a code next time */
    }
};

export const authAPI = {
    login: (email, password) =>
        api.post("/auth/login", { email, password, device_token: readDevices()[String(email).toLowerCase()] || null }),
    verify2fa: (mfa_token, code, remember) => api.post("/auth/2fa/verify", { mfa_token, code, remember }),
    getMe: () => api.get("/auth/me"),
};

// Two-factor sign-in (signed in)
export const mfaAPI = {
    status: () => api.get("/auth/2fa/status"),
    setup: () => api.post("/auth/2fa/setup"),
    enable: (code) => api.post("/auth/2fa/enable", { code }),
    newRecoveryCodes: (code) => api.post("/auth/2fa/recovery-codes", { code }),
    forgetDevice: (id) => api.delete(`/auth/2fa/devices/${id}`),
    disable: (password, code) => api.post("/auth/2fa/disable", { password, code }),
};

// Users
export const usersAPI = {
    getAll: () => api.get("/users"),
    create: (data) => api.post("/users", data),
    delete: (id) => api.delete(`/users/${id}`),
    update: (id, data) => api.put(`/users/${id}`, data),
    setTabs: (id, hiddenTabs, rights) => api.put(`/users/${id}/tabs`, { hidden_tabs: hiddenTabs, ...(rights && { rights }) }),
    // A password change ends every other session and returns this one's new token.
    updateProfile: (data) =>
        api.put("/profile", data).then((r) => {
            if (r.data?.token) localStorage.setItem("token", r.data.token);
            return r;
        }),
};

// Celebrities
export const celebritiesAPI = {
    getAll: (params) => api.get("/celebrities", { params }),
    salesMonths: () => api.get("/celebrities/sales-months"),
    getById: (id) => api.get(`/celebrities/${id}`),
    setSocials: (id, socials) => api.put(`/celebrities/${id}/socials`, socials),
    create: (data) => api.post("/celebrities", data),
    update: (id, data) => api.put(`/celebrities/${id}`, data),
    delete: (id) => api.delete(`/celebrities/${id}`),
    transfer: (id, newAmId) => api.post(`/celebrities/${id}/transfer`, { new_am_id: newAmId }),
    bulkTransfer: (celebrityIds, newAmId) => api.post("/celebrities/bulk-transfer", { celebrity_ids: celebrityIds, new_am_id: newAmId }),
    // Copies each celebrity's photo from boutiqaat.com (matched by boutique id). Can take a minute.
    syncPhotos: () => api.post("/celebrities/sync-photos", null, { timeout: 300000 }),
    bulkUpload: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/celebrities/bulk-upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
};

// Sales
export const salesAPI = {
    getByCelebrity: (id) => api.get(`/sales/${id}`),
    clearForCelebrity: (id, periodStart, periodEnd) => {
        const params = new URLSearchParams();
        if (periodStart) params.append("period_start", periodStart);
        if (periodEnd) params.append("period_end", periodEnd);
        return api.delete(`/sales/${id}/clear?${params.toString()}`);
    },
    deleteRecord: (saleId) => api.delete(`/sales/record/${saleId}`),
    bulkDelete: (saleIds) => api.post("/sales/bulk-delete", { sale_ids: saleIds }),
    getAll: () => api.get("/sales/history/all"),
};

// Mail (SRM): the digests the bot works from, read in the web app
export const mailAPI = {
    list: (params) => api.get("/mail", { params }),
    get: (id) => api.get(`/mail/${id}`),
    setStatus: (id, status) => api.patch(`/mail/${id}`, { status }),
    makeTask: (id) => api.post(`/mail/${id}/task`),
    attachments: (id) => api.get(`/mail/${id}/attachments`),
    goodsXlsx: (id, amEmail) =>
        api.get(`/mail/${id}/goods.xlsx`, { params: amEmail === undefined ? {} : { am_email: amEmail }, responseType: "blob" }),
    download: (id, attId) => api.get(`/mail/${id}/attachments/${attId}/download`, { responseType: "blob" }),
    reprocess: (id, resend = false) => api.post(`/mail/${id}/reprocess`, null, { params: { resend }, timeout: 120000 }),
    uploadAttachment: (id, fileList) => {
        const formData = new FormData();
        Array.from(fileList).forEach((f) => formData.append("files", f));
        return api.post(`/mail/${id}/upload-attachment`, formData, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
        });
    },
};

// Bundles
// What to add to a boutique (team sellers + boutiqaat.com catalogue, other
// celebrities' exclusives left out) + the Import Products file for ticked items.
export const addSuggestionsAPI = {
    last: (celebrityId) => api.get(`/add-suggestions/${celebrityId}`),
    build: (celebrityId, limit = 40, minSoh = 0, discounted = false, cats = {}) =>
        api.post(`/add-suggestions/${celebrityId}`, null, {
            params: { limit, min_soh: minSoh, discounted, cats_in: (cats.cats_in || []).join("|"), cats_out: (cats.cats_out || []).join("|") },
            timeout: 900000,
        }),
    categories: () => api.get("/catalog/categories"),
    csv: (celebrityId, skus) => api.post(`/add-suggestions/${celebrityId}/import.csv`, { skus }, { responseType: "blob" }),
    // "catalog" = boutiqaat.com once a day, "boutiques" = every celebrity's boutique.
    sync: (what) => api.post(`/site-sync/${what}`),
    syncStatus: () => api.get("/site-sync"),
    syncedBoutiques: () => api.get("/site-sync/boutiques/list"),
    priceChanges: (day, f = {}) => api.get("/price-changes", { params: { ...(day ? { day } : {}), ...f }, timeout: 120000 }),
    priceChangesSend: (body) => api.post("/price-changes/send", body, { timeout: 600000 }),
    priceChangesXlsx: (day, kind = "all", onlyOurs = false, f = {}) =>
        api.get("/price-changes/export.xlsx", {
            params: { ...(day ? { day } : {}), kind, only_ours: onlyOurs, ...f },
            responseType: "blob",
            timeout: 600000,
        }),
    newProducts: (day, f = {}) => api.get("/new-products", { params: { ...(day ? { day } : {}), ...f }, timeout: 120000 }),
    topSelling: (day, period = 1, f = {}) => api.get("/top-selling", { params: { ...(day ? { day } : {}), period, ...f }, timeout: 120000 }),
    topSellingXlsx: (day, period = 1, f = {}) =>
        api.get("/top-selling/export.xlsx", { params: { ...(day ? { day } : {}), period, ...f }, responseType: "blob", timeout: 600000 }),
    newProductsXlsx: (day, f = {}) =>
        api.get("/new-products/export.xlsx", { params: { ...(day ? { day } : {}), ...f }, responseType: "blob", timeout: 600000 }),
    // Each AM's bulk-upload file (all their celebrities) on WhatsApp.
    amPrepare: (top, minSoh, discounted, cats = {}) => api.post("/add-suggestions-am/prepare", { top, min_soh: minSoh, discounted, ...cats }),
    amPrepareStatus: () => api.get("/add-suggestions-am/prepare"),
    amPreview: (top, minSoh, discounted, cats = {}) =>
        api.post("/add-suggestions-am/preview", { top, min_soh: minSoh, discounted, ...cats }, { timeout: 120000 }),
    amSend: (top, minSoh, discounted, ams, cats = {}) =>
        api.post("/add-suggestions-am/send", { top, min_soh: minSoh, discounted, ams, ...cats }, { timeout: 900000 }),
    amCsv: (email, top, minSoh, discounted, cats = {}) =>
        api.post(`/add-suggestions-am/${encodeURIComponent(email)}/import.csv`, { top, min_soh: minSoh, discounted, ...cats }, {
            responseType: "blob",
        }),
    csvAll: (top = 20, minSoh = 0, discounted = false, cats = {}) =>
        api.post("/add-suggestions-all/import.csv", { top, min_soh: minSoh, discounted, ...cats }, { responseType: "blob", timeout: 1800000 }),
};

// Scrape: any boutiqaat.com listing -> Excel of the products not exclusive and not sold out.
export const scrapeAPI = {
    start: (url) => api.post("/scrape", { url }),
    status: (id) => api.get(`/scrape/${id}`),
    list: () => api.get("/scrapes"),
    xlsx: (id) => api.get(`/scrape/${id}/export.xlsx`, { responseType: "blob", timeout: 300000 }),
};

// Boutique sorting: the first 100 places (rows of two) + the Import Products file.
// Sort performance: the AMs' boutique moves, day to day, judged on pieces sold.
export const sortPerformanceAPI = {
    all: () => api.get("/sort-performance", { timeout: 180000 }),
    one: (celebrityId) => api.get(`/sort-performance/${celebrityId}`, { timeout: 120000 }),
};

export const boutiqueSortAPI = {
    last: (celebrityId) => api.get(`/boutique-sort/${celebrityId}`),
    build: (celebrityId, places = 100) => api.post(`/boutique-sort/${celebrityId}`, null, { params: { places }, timeout: 300000 }),
    csv: (celebrityId) => api.get(`/boutique-sort/${celebrityId}/import.csv`, { responseType: "blob" }),
    items: (celebrityId) => api.get(`/boutique-sort/${celebrityId}/items`, { timeout: 120000 }),
    plan: (celebrityId, rules) => api.post(`/boutique-sort/${celebrityId}/plan`, { rules }, { timeout: 600000 }),
    save: (celebrityId, skus, rules, bottom = []) => api.put(`/boutique-sort/${celebrityId}/plan`, { skus, rules, bottom }),
    instructions: (celebrityId, skus, text) => api.post(`/boutique-sort/${celebrityId}/instructions`, { skus, text }, { timeout: 300000 }),
};

// Sales at the price they sold at on boutiqaat.com (discounts show only there).
export const salesTrackingAPI = {
    day: (day) => api.get("/sales-tracking", { params: day ? { day } : {}, timeout: 120000 }),
    month: (month) => api.get("/sales-tracking", { params: { month }, timeout: 180000 }),
    setTarget: (body) => api.put("/sales-tracking/target", body),
    uploadTargets: (file, month) => {
        const fd = new FormData();
        fd.append("file", file);
        if (month) fd.append("month", month);
        return api.post("/sales-tracking/targets/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
    },
    check: (day, recheck = false) => api.post("/sales-tracking/check", { day, recheck }),
    status: () => api.get("/sales-tracking/check"),
};

// Schedules + Stop for the boutiqaat.com jobs (prices, catalog, boutiques, exclusives, categories).
export const amPackAPI = {
    get: () => api.get("/am-pack"),
    save: (body) => api.put("/am-pack", body),
    run: () => api.post("/am-pack/run"),
    preview: () => api.post("/am-pack/preview", null, { timeout: 600000 }),
};

// Compare prices: our products vs competitor shops (Kuwait + GCC)
export const compareAPI = {
    list: () => api.get("/compare-prices", { timeout: 120000 }),
    run: (body = {}) => api.post("/compare-prices/run", body),
    addSite: (s) => api.post("/compare-prices/sites", s),
    editSite: (id, s) => api.put(`/compare-prices/sites/${encodeURIComponent(id)}`, s),
    deleteSite: (id) => api.delete(`/compare-prices/sites/${encodeURIComponent(id)}`),
    testSite: (body) => api.post("/compare-prices/sites/test", body, { timeout: 180000 }),
    confirm: (id, ok) => api.put(`/compare-prices/results/${encodeURIComponent(id)}`, { ok }),
    settings: (s) => api.put("/compare-prices/settings", s),
    // The cheaper-elsewhere Excel with the page's filters: {where: all|KW|GCC, shop, min_diff}
    excel: (f = {}) =>
        api.get("/compare-prices/export.xlsx", { params: { cheaper_only: true, ...f }, responseType: "blob", timeout: 300000 }),
    // Purchasing people who get the cheaper-elsewhere file on WhatsApp (their categories).
    purchasers: () => api.get("/compare-prices/purchasers"),
    savePurchasers: (purchasers) => api.put("/compare-prices/purchasers", { purchasers }),
    sendPurchasers: (body) => api.post("/compare-prices/purchasers/send", body, { timeout: 600000 }),
};

// Competitors' top sellers (Compare prices → Top selling): {group, site, signal, q, day}
export const marketAPI = {
    top: (params = {}) => api.get("/market/top", { params, timeout: 120000 }),
    run: () => api.post("/market/run"),
};

// One /jobs request for every ⏰ control on a screen: Suggestions mounts 3, Sales tracking 2, the timers
// dialog one per row, each polling every 15 s — calls within 2 s share one answer (py-per 2026-11-02).
let _jobsCall = null;
let _jobsAt = 0;
const listJobs = () => {
    if (_jobsCall && Date.now() - _jobsAt < 2000) return _jobsCall;
    _jobsAt = Date.now();
    _jobsCall = api.get("/jobs");
    _jobsCall.catch(() => {
        _jobsCall = null;
    });
    return _jobsCall;
};

export const jobsAPI = {
    list: listJobs,
    setSchedule: (key, schedule) => api.put(`/jobs/${key}/schedule`, schedule),
    stop: (key) => api.post(`/jobs/${key}/stop`),
    // Every timer over the next 24 h, what runs now and what waits (Dashboard → Timers).
    timeline: () => api.get("/jobs/timeline", { timeout: 60000 }),
};

export const socialAPI = {
    coverage: (params) => api.get("/social/coverage", { params, timeout: 60000 }),
    settings: () => api.get("/social/settings"),
    saveSettings: (s) => api.put("/social/settings", s),
    checkNow: () => api.post("/social/check-now"),
    checkStatus: () => api.get("/social/check-status"),
    checkBoutiqaat: (id, yes) => api.put(`/social/snaps/${encodeURIComponent(id)}/boutiqaat`, { yes }),
    names: () => api.get("/social/names"),
    fixName: (key, brand) => api.put(`/social/names/${encodeURIComponent(key)}`, { brand }),
    studyNames: () => api.post("/social/names/study"),
    nameBrands: (q) => api.get("/social/names/brands", { params: { q } }),
    nameSnaps: (key) => api.get(`/social/names/${encodeURIComponent(key)}/snaps`),
    accounts: () => api.get("/social/accounts"),
    setAccount: (id, snapchat) => api.put(`/social/accounts/${id}`, { snapchat }),
    competitor: (params) => api.get("/social/competitor/coverage", { params, timeout: 60000 }),
    igStatus: () => api.get("/social/instagram"),
    igLogin: (username, password) => api.post("/social/instagram/login", { username, password }, { timeout: 90000 }),
    igCode: (username, code) => api.post("/social/instagram/code", { username, code }, { timeout: 90000 }),
    igDisconnect: () => api.post("/social/instagram/disconnect"),
    ttStatus: () => api.get("/social/tiktok"),
    ttDisconnect: () => api.post("/social/tiktok/disconnect"),
    runCompetitor: (am) => api.post("/social/competitor/run", { am: am || null }),
    stopCompetitor: () => api.post("/social/competitor/stop"),
};

export const salesEventsAPI = {
    list: () => api.get("/sales-events"),
    add: (e) => api.post("/sales-events", e),
    edit: (id, e) => api.put(`/sales-events/${id}`, e),
    remove: (id) => api.delete(`/sales-events/${id}`),
};

// Projects from the project app (project.jarves.net): new items each AM builds with a celebrity.
// What our celebrities sell that is running out on boutiqaat.com (features/stock_alarm.py).
export const stockAlarmAPI = {
    list: () => api.get("/stock-alarm", { timeout: 60000 }),
    xlsx: (params) => api.get("/stock-alarm/export.xlsx", { params, responseType: "blob", timeout: 300000 }),
    settings: () => api.get("/stock-alarm/settings"),
    saveSettings: (body) => api.put("/stock-alarm/settings", body),
};

// Screenshots of boutiqaat products → their SKUs, exclusive / sold out removed (features/photo_skus.py).
export const photoSkusAPI = {
    // One part of a batch (nginx takes 25 MB a request): job = the id the first part got, last = start reading.
    upload: (files, { job = "", last = true } = {}, onProgress) => {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        fd.append("job", job);
        fd.append("last", last ? "true" : "false");
        return api.post("/photo-skus", fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 600000,
            onUploadProgress: onProgress,
        });
    },
    list: () => api.get("/photo-skus"),
    get: (id) => api.get(`/photo-skus/${id}`),
    remove: (id) => api.delete(`/photo-skus/${id}`),
    xlsx: (id) => api.get(`/photo-skus/${id}/export.xlsx`, { responseType: "blob", timeout: 300000 }),
};

// What sells for similar celebrities that a boutique doesn't list (features/lookalike_gaps.py).
export const lookalikeGapsAPI = {
    list: () => api.get("/lookalike-gaps", { timeout: 120000 }),
    one: (id) => api.get(`/lookalike-gaps/${id}`, { timeout: 120000 }),
    xlsx: (params) => api.get("/lookalike-gaps-export.xlsx", { params, responseType: "blob", timeout: 300000 }),
    csv: (params) => api.get("/lookalike-gaps-import.csv", { params, responseType: "blob", timeout: 120000 }),
};

// The Sunday brief: the week's 10 actions worth the most KWD (features/srm_brief.py).
export const srmBriefAPI = {
    get: (week) => api.get("/srm-brief", { params: { week }, timeout: 180000 }),
    rebuild: () => api.post("/srm-brief/rebuild", null, { timeout: 300000 }),
    done: (body) => api.put("/srm-brief/done", body),
};

// The week per AM: celebrities vs their usual and vs the team (features/am_scorecard.py).
export const amScorecardAPI = {
    get: (params) => api.get("/am-scorecard", { params, timeout: 120000 }),
    xlsx: (params) => api.get("/am-scorecard/export.xlsx", { params, responseType: "blob", timeout: 300000 }),
};

// Briefs for the purchasing team, brand by brand (features/purchase_briefs.py).
export const purchaseBriefsAPI = {
    list: () => api.get("/purchase-briefs", { timeout: 120000 }),
    one: (id) => api.get("/purchase-briefs/one", { params: { id }, timeout: 120000 }),
    setStatus: (id, body) => api.put("/purchase-briefs/status", body, { params: { id } }),
    xlsx: (id) => api.get("/purchase-briefs/export.xlsx", { params: { id }, responseType: "blob", timeout: 300000 }),
};

// The month's exclusive list split between the AMs (features/exclusive_picks.py).
export const exclusivePicksAPI = {
    // One filtered, sorted page (limit/offset + the page's filters); without params the whole month.
    list: (month, params = {}) => api.get("/exclusives/picks", { params: { ...(month ? { month } : {}), ...params }, timeout: 60000 }),
    upload: (file, month) => {
        const fd = new FormData();
        fd.append("file", file);
        if (month) fd.append("month", month);
        return api.post("/exclusives/picks/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
    },
    check: (month) => api.post("/exclusives/picks/check", null, { params: { month } }),
    outside: (month) => api.post("/exclusives/picks/outside", null, { params: { month }, timeout: 120000 }),
    pass: (id, reason, toAm) => api.post(`/exclusives/picks/${encodeURIComponent(id)}/pass`, { reason, ...(toAm && { to_am: toAm }) }),
    move: (id, am_email) => api.post(`/exclusives/picks/${encodeURIComponent(id)}/move`, { am_email }),
    item: (id) => api.get(`/exclusives/picks/item/${encodeURIComponent(id)}`),
    settings: () => api.get("/exclusives/picks/settings"),
    womenBrands: () => api.get("/exclusives/picks/women-brands", { timeout: 120000 }),
    saveSettings: (body) => api.put("/exclusives/picks/settings", body, { timeout: 180000 }),
};

export const projectsAPI = {
    list: (refresh = false) => api.get("/am-projects", { params: refresh ? { refresh: 1 } : {}, timeout: 60000 }),
    // Departments: lead time + months of stock to hold, per department and per boutiqaat category in it.
    departments: () => api.get("/am-projects/departments"),
    saveDepartment: (d) => api.put("/am-projects/departments", d),
    deleteDepartment: (name) => api.delete(`/am-projects/departments/${encodeURIComponent(name)}`),
    // Set Online (and the boutiqaat.com photo) in the project app for SKUs already live.
    syncOnline: () => api.post("/am-projects/sync-online", null, { timeout: 180000 }),
    // Names from boutiqaat.com for every project SKU, written into the project app.
    fetchNames: () => api.post("/am-projects/fetch-names", null, { timeout: 600000 }),
};

// AM item requests (Projects → Pending / Approved / Rejected). Fashion Tech Fares /
// Abdullah ones live in the project app; the rest only here.
export const requestsAPI = {
    list: () => api.get("/am-requests", { timeout: 60000 }),
    meta: () => api.get("/am-requests/meta"),
    setLinked: (departments) => api.put("/am-requests/linked", { departments }),
    create: (body) => api.post("/am-requests", body, { timeout: 60000 }),
    edit: (id, body) => api.patch(`/am-requests/${id}`, body),
    remove: (id) => api.delete(`/am-requests/${id}`),
    approve: (id) => api.post(`/am-requests/${id}/approve`),
    reject: (id, reason) => api.post(`/am-requests/${id}/reject`, { reason }),
    resubmit: (id) => api.post(`/am-requests/${id}/resubmit`),
    company: (id, companyId) => api.post(`/am-requests/${id}/company`, { company_id: companyId }),
    step: (id, body) => api.post(`/am-requests/${id}/step`, body),
    timeline: (id) => api.get(`/am-requests/${id}/timeline`),
    scrape: (url) => api.post("/am-requests/scrape", { url }, { timeout: 90000 }),
    photo: (file, department) => {
        const fd = new FormData();
        fd.append("file", file);
        return api.post("/am-requests/photo", fd, {
            params: { department },
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        });
    },
};

export const bundlesAPI = {
    // Bundles as they sold (BDL codes in the sales), with boutiqaat.com photo/stock.
    sales: () => api.get("/bundle-sales", { timeout: 120000 }),
    refreshSite: () => api.post("/bundle-sales/refresh", null, { timeout: 600000 }),
    getAll: () => api.get("/bundles"),
    getById: (id) => api.get(`/bundles/${id}`),
    create: (data) => api.post("/bundles", data),
    update: (id, data) => api.put(`/bundles/${id}`, data),
    delete: (id) => api.delete(`/bundles/${id}`),
    bulkUpload: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/bundles/bulk-upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
};

// Received Goods
export const receivedGoodsAPI = {
    upload: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/received-goods/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    getAll: () => api.get("/received-goods"),
};

// Exclusive Items
export const exclusiveItemsAPI = {
    upload: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/exclusive-items/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    getAll: () => api.get("/exclusive-items"),
};

// Received-goods and enabled-SKU lists by date (Received Goods page)
export const goodsListsAPI = {
    autoSend: (kind) => api.get(`/goods/auto-send/${kind}`),
    setAutoSend: (kind, body) => api.put(`/goods/auto-send/${kind}`, body),
    lists: (kind) => api.get("/goods/lists", { params: { kind } }),
    items: (digestId) => api.get(`/goods/lists/${digestId}/items`),
    reportDays: (kind) => api.get("/goods/report-days", { params: { kind } }),
    trials: () => api.get("/goods/trials"),
    sendReport: (kind, days, channel = "telegram", ams = null) =>
        api.post("/goods/report", { kind, days, send: true, channel, ams }, { timeout: 300000 }),
    previewReport: (kind, days) => api.post("/goods/report", { kind, days, send: false }, { timeout: 180000 }),
    reportXlsx: (kind, days) =>
        api.get("/goods/report.xlsx", { params: { kind, days: days.join(",") }, responseType: "blob", timeout: 180000 }),
    // boutiqaat.com celebrity-product mapping CSV for the same report.
    reportCsv: (kind, days, ams = []) =>
        api.get("/goods/report.csv", {
            params: { kind, days: days.join(","), ams: ams.join(",") }, responseType: "blob", timeout: 180000,
        }),
};

// Exclusives as boutiqaat.com lists them (Exclusive Items page)
export const siteExclusivesAPI = {
    // period: {from, to} (YYYY-MM-DD, either may be empty) — what "Sold" and the verdict count
    list: (period = {}) =>
        api.get("/exclusives/site", { params: { ...(period.from ? { start: period.from } : {}), ...(period.to ? { end: period.to } : {}) } }),
    update: () => api.post("/exclusives/sync-site", null, { params: { send: false } }),
    status: () => api.get("/exclusives/sync-site"),
    sendReport: (channel = "telegram") =>
        api.post("/exclusives/report", null, { params: { send: true, channel }, timeout: 300000 }),
    // Mark a celebrity's exclusive SKU as not counted against the monthly limit.
    setLimitExempt: (celebrity_id, sku, exempt) => api.put("/exclusives/limit-exempt", { celebrity_id, sku, exempt }),
};

// The platform's exclusivity request export: live periods + expiry alerts
export const exclusivityAPI = {
    // From the full exclusivity report: waiting approval / rejected requests.
    requests: (status = "waiting", days = 30) => api.get("/exclusives/requests", { params: { status, days }, timeout: 120000 }),
    requestsXlsx: (status = "waiting", days = 30, am = "all", departments = []) =>
        api.get("/exclusives/requests/export.xlsx", {
            params: { status, days, am, departments: departments.join("|") },
            responseType: "blob",
            timeout: 600000,
        }),
    upload: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/exclusives/requests/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
        });
    },
    expiring: (days = 30) => api.get("/exclusives/expiring", { params: { days } }),
    // Every team celebrity over / at the limit with its AM (SRM, or an AM with the team_limits right).
    teamLimits: () => api.get("/exclusives/limits/team", { timeout: 120000 }),
    // Monthly "Exclusivity Limit" sheet: max exclusives per celebrity (bundles not counted).
    uploadLimits: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/exclusives/limits/upload", formData, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
        });
    },
    alert: () => api.post("/exclusives/expiring/alert"),
    // Good sellers to extend, one Excel per AM (sent only on request).
    extendPreview: (past = true) => api.post("/exclusives/extend-report", { send: false, past }, { timeout: 180000 }),
    // sections: null = everything, else ["soon", "past", "removed", "over", "missing", "waiting"]
    extendSend: (channel, ams = null, past = true, sections = null) =>
        api.post("/exclusives/extend-report", { send: true, channel, ams, past, sections }, { timeout: 600000 }),
    extendXlsx: (past = true, sections = null) =>
        api.get("/exclusives/extend-report.xlsx", {
            params: { past, ...(sections ? { sections: sections.join("|") } : {}) },
            responseType: "blob",
            timeout: 180000,
        }),
};

// The SRM's own WhatsApp, linked by QR, and each account manager's number
export const whatsappAPI = {
    status: () => api.get("/whatsapp/status"),
    logout: () => api.post("/whatsapp/logout"),
    contacts: () => api.get("/whatsapp/contacts"),
    setContact: (email, whatsapp) => api.put("/whatsapp/contacts", { email, whatsapp }),
    test: (email) => api.post("/whatsapp/test", { email }),
};

// Suggestions
export const suggestionsAPI = {
    getSettings: () => api.get("/suggestions/settings"),
    updateSettings: (data) => api.put("/suggestions/settings", data),
    generate: (source = "all") => api.post(`/suggestions/generate?source=${source}`),
    generateAI: (source = "all") => api.post(`/suggestions/ai-generate?source=${source}`),
    getAll: (celebrityId, source) => {
        const params = new URLSearchParams();
        if (celebrityId) params.append("celebrity_id", celebrityId);
        if (source) params.append("source", source);
        return api.get(`/suggestions?${params.toString()}`);
    },
};

// Dashboard
export const dashboardAPI = {
    getStats: () => api.get("/dashboard/stats"),
    getTrends: () => api.get("/dashboard/trends"),
    month: (month) => api.get("/dashboard/month", { params: month ? { month } : {}, timeout: 60000 }),
    why: (params) => api.get("/dashboard/why", { params, timeout: 60000 }),
};

// Admin
export const adminAPI = {
    getSettings: () => api.get("/admin/settings"),
    updateSettings: (data) => api.put("/admin/settings", data),
};

// Security page (SRM only)
export const securityAPI = {
    overview: () => api.get("/admin/security/overview"),
    saveConfig: (data) => api.put("/admin/security/config", data),
    bans: () => api.get("/admin/security/ip-bans"),
    ban: (data) => api.post("/admin/security/ip-bans", data),
    unban: (ip) => api.delete(`/admin/security/ip-bans/${encodeURIComponent(ip)}`),
    allowlist: () => api.get("/admin/security/allowlist"),
    trust: (data) => api.post("/admin/security/allowlist", data),
    untrust: (ip) => api.delete(`/admin/security/allowlist/${encodeURIComponent(ip)}`),
    users: () => api.get("/admin/security/users"),
    userAction: (id, action) => api.post(`/admin/security/users/${id}/${action}`),     // unlock|block|unblock|sign-out|reset-2fa
    audit: (params) => api.get("/admin/security/audit", { params }),
};

// Comparison
export const comparisonAPI = {
    compareCelebrities: (ids) => api.get(`/compare/celebrities?celebrity_ids=${ids.join(",")}`),
};

// Reports
export const reportsAPI = {
    export: (type, format = "json") => api.get(`/reports/export?report_type=${type}&format=${format}`),
};

// Celebrity Discovery (Suggested Celebrities)
export const discoveryAPI = {
    getSettings: () => api.get("/discovery/settings"),
    updateSettings: (data) => api.put("/discovery/settings", data),
    getBoutiqaatList: () => api.get("/discovery/boutiqaat"),
    refreshBoutiqaat: () => api.post("/discovery/boutiqaat/refresh"),
    diagnoseBoutiqaat: () => api.get("/discovery/boutiqaat/diagnose"),
    debugExtractSocial: (platform, handle) =>
        api.post("/discovery/extract-social/debug", { platform, handle }),
    updateBoutiqaatCelebrity: (nameNormalized, data) =>
        api.patch(`/discovery/boutiqaat/${encodeURIComponent(nameNormalized)}`, data),
    runDiscovery: () => api.post("/discovery/run"),
    getRun: (id) => api.get(`/discovery/runs/${id}`),
    getLatestRun: () => api.get("/discovery/runs/latest"),
    listSuggestions: (status, tier) => {
        const params = new URLSearchParams();
        if (status) params.append("status", status);
        if (tier) params.append("tier", tier);
        return api.get(`/discovery/suggestions?${params.toString()}`);
    },
    getSuggestion: (id) => api.get(`/discovery/suggestions/${id}`),
    approve: (id) => api.post(`/discovery/suggestions/${id}/approve`),
    ignore: (id) => api.post(`/discovery/suggestions/${id}/ignore`),
    unignore: (id) => api.post(`/discovery/suggestions/${id}/unignore`),
    refreshOne: (id) => api.post(`/discovery/suggestions/${id}/refresh`),
    markExisting: (id) => api.post(`/discovery/suggestions/${id}/mark-existing`),
    updateSuggestion: (id, data) => api.patch(`/discovery/suggestions/${id}`, data),
    remove: (id) => api.delete(`/discovery/suggestions/${id}`),
    createShareLink: (label, type = "review", staff_name = null) =>
        api.post("/discovery/share", { label, type, staff_name }),
    listShareLinks: () => api.get("/discovery/share"),
    revokeShareLink: (token) => api.delete(`/discovery/share/${token}`),
    uploadPhoto: (file) => {
        const formData = new FormData();
        formData.append("file", file);
        return api.post("/discovery/uploads/photo", formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    migrateStaffPendingToGenerated: () =>
        api.post("/discovery/admin/migrate-staff-pending-to-generated"),
    resolveSocialUrl: (platform, url) =>
        api.post("/discovery/resolve-social-url", { platform, url }),
};

// Public share endpoints — no auth required
const shareAxios = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
});

export const sharedDiscoveryAPI = {
    info: (token) => shareAxios.get(`/share/discovery/${token}/info`),
    list: (token, status = "pending", tier = null) => {
        const params = new URLSearchParams();
        params.set("status", status);
        if (tier) params.set("tier", tier);
        return shareAxios.get(`/share/discovery/${token}/suggestions?${params.toString()}`);
    },
    approve: (token, id, tier = "celebrity") =>
        shareAxios.post(`/share/discovery/${token}/suggestions/${id}/approve?tier=${tier}`),
    ignore: (token, id) => shareAxios.post(`/share/discovery/${token}/suggestions/${id}/ignore`),
    markExisting: (token, id) => shareAxios.post(`/share/discovery/${token}/suggestions/${id}/mark-existing`),
    submit: (token, payload) => shareAxios.post(`/share/discovery/${token}/submit`, payload),
    checkDuplicate: (token, payload) =>
        shareAxios.post(`/share/discovery/${token}/check-duplicate`, payload),
    extractSocial: (token, payload) =>
        shareAxios.post(`/share/discovery/${token}/extract-social`, payload),
    resolveSocialUrl: (token, platform, url) =>
        shareAxios.post(`/share/discovery/${token}/resolve-social-url`, { platform, url }),
    uploadPhoto: (token, file) => {
        const formData = new FormData();
        formData.append("file", file);
        return shareAxios.post(`/share/discovery/${token}/uploads/photo`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
};

export default api;

export const skuExcludeAPI = {
    get: () => api.get("/sku-exclude"),
    // categories: [{category, sub?, word?, gender: "women" | "men" | ""}] (undefined = keep the saved ones)
    save: (prefixes, categories) => api.put("/sku-exclude", { prefixes, ...(categories ? { categories } : {}) }),
};

// A celebrity's specialty (dentist, gym trainer, Arabic perfumer…) and the products that belong to it.
export const specialtyAPI = {
    presets: () => api.get("/specialties"),
    get: (id) => api.get(`/celebrities/${encodeURIComponent(id)}/specialty`, { timeout: 60000 }),
    // body: { specialties (his work), interests (what he likes), profession, keywords }
    save: (id, body) => api.put(`/celebrities/${encodeURIComponent(id)}/specialty`, body),
    useAi: (id) => api.put(`/celebrities/${encodeURIComponent(id)}/specialty`, { use_ai: true }),
    // The AI reads his Snapchat profile + analysed snaps and picks the specialty (backend/features/specialty_ai.py).
    ask: (id) => api.post(`/celebrities/${encodeURIComponent(id)}/specialty/ai`, {}, { timeout: 240000 }),
    runAll: (force = false) => api.post(`/specialty/ai/run`, {}, { params: { force } }),
    status: () => api.get(`/specialty/ai/status`),
    csv: (id) => api.get(`/celebrities/${encodeURIComponent(id)}/specialty/missing.csv`, { responseType: "blob" }),
};

export const restrictionsAPI = {
    get: (id) => api.get(`/celebrities/${encodeURIComponent(id)}/restrictions`),
    save: (id, rules) => api.put(`/celebrities/${encodeURIComponent(id)}/restrictions`, { rules }),
    options: () => api.get("/restrictions/options"),
    brands: (q) => api.get("/restrictions/brands", { params: { q } }),
};

// Favorites: starred SKUs and brands, highlighted wherever they turn up.
// Senior category manager: his departments' boutiqaat.com products (backend/features/scm.py).
const scmGet = (path) => (params = {}) => api.get(`/scm/${path}`, { params, timeout: 120000 });
export const scmAPI = {
    departments: () => api.get("/scm/departments"),
    setDepartmentRules: (name, rules) => api.put(`/scm/departments/${encodeURIComponent(name)}/rules`, rules),
    setDepartments: (userId, departments) => api.put(`/scm/users/${encodeURIComponent(userId)}/departments`, { departments }),
    dashboard: (dept) => api.get("/scm/dashboard", { params: dept ? { dept } : {}, timeout: 120000 }),
    stock: scmGet("stock"),
    slow: scmGet("slow"),
    bundles: scmGet("bundles"),
    exclusives: scmGet("exclusives"),
    projects: (dept) => api.get("/scm/projects", { params: dept ? { dept } : {}, timeout: 120000 }),
    prices: scmGet("prices"),
    sites: (dept) => api.get("/scm/sites", { params: dept ? { dept } : {} }),
    addSite: (body, dept) => api.post("/scm/sites", body, { params: dept ? { dept } : {} }),
    editSite: (id, body, dept) => api.put(`/scm/sites/${encodeURIComponent(id)}`, body, { params: dept ? { dept } : {} }),
    removeSite: (id) => api.delete(`/scm/sites/${encodeURIComponent(id)}`),
    testSite: (body) => api.post("/scm/sites/test", body, { timeout: 120000 }),
    // His own projects (a team outside the app): steps with dates, alarms past them.
    ownProjects: (params = {}) => api.get("/scm/own-projects", { params }),
    ownAlarms: () => api.get("/scm/own-projects/alarms"),
    ownCreate: (body) => api.post("/scm/own-projects", body),
    skuLookup: (sku) => api.get("/scm/sku-lookup", { params: { sku } }),
    ownEdit: (id, body) => api.patch(`/scm/own-projects/${encodeURIComponent(id)}`, body),
    ownDone: (id, body) => api.post(`/scm/own-projects/${encodeURIComponent(id)}/done`, body),
    ownExtend: (id, body) => api.post(`/scm/own-projects/${encodeURIComponent(id)}/extend`, body),
    ownCancel: (id, body) => api.post(`/scm/own-projects/${encodeURIComponent(id)}/cancel`, body),
    ownReopen: (id) => api.post(`/scm/own-projects/${encodeURIComponent(id)}/reopen`),
    boutiques: (q) => api.get("/scm/boutiques", { params: { q }, timeout: 60000 }),
    addBundle: (body) => api.post("/scm/bundles", body, { timeout: 180000 }),
    removeBundle: (id) => api.delete(`/scm/bundles/${encodeURIComponent(id)}`),
    stepTemplate: (flow = "new", assignee) => api.get("/scm/step-template", { params: { flow, ...(assignee && { assignee }) } }),
    saveStepTemplate: (steps, flow = "new") => api.put("/scm/step-template", { steps }, { params: { flow } }),
};

export const favoritesAPI = {
    list: (userId) => api.get("/favorites", { params: userId ? { user_id: userId } : {} }),
    add: (kind, value, extra = {}) => api.post("/favorites", { kind, value, ...extra }),
    remove: (id) => api.delete(`/favorites/${encodeURIComponent(id)}`),
    toggle: (kind, value, extra = {}) => api.post("/favorites/toggle", { kind, value, ...extra }),
    brands: (q) => api.get("/favorites/brands", { params: { q } }),
    hits: (days = 14, userId) => api.get("/favorites/hits", { params: { days, ...(userId && { user_id: userId }) }, timeout: 120000 }),
    team: (days = 14) => api.get("/favorites/team", { params: { days }, timeout: 120000 }),
};
